/**
 * Needs to be kept in sync with DB enum values.
 */
export const homePlanCategoryValues = ["prospect", "contract", "testing", "closed"] as const;
export type HomePlanCategory = (typeof homePlanCategoryValues)[number];

/**
 * Needs to be kept in sync with DB enum values.
 */
export const homePlanSourceValues = ["web_lead", "sales_agent"] as const;
export type HomePlanSource = (typeof homePlanSourceValues)[number];

/**
 * Needs to be kept in sync with DB enum values.
 */
export const homePlanStatusValues = ["PREP", "PRE_CONSTRUCTION", "CONSTRUCTION"] as const;
export type HomePlanStatus = (typeof homePlanStatusValues)[number];

/**
 * Generated and uploaded image state for a plan at any level.
 *
 * Studio docs, master plans and community plans have selectable elevations, so they populate
 * `generatedElevations`. A home plan's elevation is already fixed in its `optionChoices`, so it has
 * one image and never populates that map -- only `thumbnailHash`.
 *
 * `thumbnailUrl` (stored outside this type) therefore means the same thing at every level: the
 * entity's default-or-chosen elevation image. Plan levels alias it to
 * `generatedElevations[defaultElevationId]`; see `resolveThumbnailUrl` in `planImageService`.
 */
export interface PlanImages {
  // Generated by Higharc
  generatedElevations?: {
    [elevationId: string]: string;
  };
  // Hash of the generation inputs that produced generatedElevations[elevationId]; a matching
  // hash on the next publish skips regeneration for that elevation.
  generatedElevationHashes?: {
    [elevationId: string]: string;
  };
  // Hash of the generation inputs that produced the entity's thumbnailUrl (stored outside
  // PlanImages); a matching hash on the next publish skips regenerating the thumbnail. Written by
  // home plans, and by the plan levels only when they have no elevation image to alias.
  thumbnailHash?: string;
  // Uploaded by users
  userElevations?: {
    [elevationId: string]: string;
  };
}

export type BuildingStats = {
  readonly elevationId: string;
  readonly elevationName: string;
  readonly bedroomCount: number;
  readonly bathroomCount: number;
  readonly totalFloorArea: number;
  readonly conditionedFloorArea: number;
  readonly garageCount?: number;
  readonly storyCount?: number;
};

type Pricing = {
  readonly optionsCount: number;
  readonly optionsPrice: number;
  readonly totalPrice: number;
  readonly basePrice: number;
  readonly lotPrice: number;
  readonly discount?: number;
};

export type HomePlanStats = BuildingStats &
  Pricing & {
    readonly elevation: string;
    readonly paletteName?: string;
    readonly statsJobId?: string;
  };

export const homePlanCustomerFieldValues = ["name", "email", "phone", "externalId"] as const;
export type HomePlanCustomerField = (typeof homePlanCustomerFieldValues)[number];

export const homePlanEventKindValues = [
  "HOME_PLAN_CREATED",
  "HOME_PLAN_EDITED",
  "HOME_PLAN_STATUS_CHANGED",
  "HOME_PLAN_CREDIT_NOT_CHARGED",
  "HOME_PLAN_COMMENT",
  "HOME_PLAN_EXPORTED",
  "HOME_PLAN_LOCK_STATUS_CHANGED",
  "HOME_PLAN_LOT_CHANGED",
  "HOME_PLAN_DETACHED",
  "HOME_PLAN_DELETED",
  "HOME_PLAN_MOVED",
  "HOME_PLAN_OPTIONS_PINNED",
  "HOME_PLAN_OPTIONS_UNPINNED",
  "HOME_PLAN_CONTRACT_CREATED",
  "HOME_PLAN_CONTRACT_UPDATED",
  "HOME_PLAN_CONTRACT_DELETED",
] as const;
export type HomePlanEventKind = (typeof homePlanEventKindValues)[number];

export const homePlanOrderByFieldValues = [
  "id",
  "planName",
  "planStatus",
  "createdAt",
  "updatedAt",
  "lockedAt",
  "detachedAt",
  "category",
  "externalId",
  "masterPlanName",
  "customerName",
  "communityName",
  "lotNumber",
  "lotSpec",
  "planSetId",
  "salesStaffName",
  "notes",
] as const;
export type HomePlanOrderByField = (typeof homePlanOrderByFieldValues)[number];
